import { forwardRef } from 'react';

import type { NumberFormatValues, NumericFormatProps } from 'react-number-format';
import { NumericFormat } from 'react-number-format';

import { cn } from '~/utils/cnTailwind.ts';
import { getSeparators } from '~/utils/getSeparators.ts';
import { getLocale } from '~/utils/locale.ts';

interface Props extends NumericFormatProps {
    decimals?: {
        maxDecimals: number;
        minDecimals: number;
    };
    isNullable?: boolean;
    setValue?: (value: number) => void;
}

const CurrencyInput = forwardRef<HTMLInputElement, Props>(
    ({ className, decimals = { maxDecimals: 2, minDecimals: 2 }, name, setValue, style, value, ...props }, ref) => {
        const locale = getLocale();

        const separators = getSeparators(locale);

        const withValidCharacters = (values: NumberFormatValues) => {
            const { floatValue, value: inputValue } = values;
            if (
                (value === 0 && !floatValue && inputValue !== '0.') ||
                (floatValue && floatValue > Number.MAX_SAFE_INTEGER)
            ) {
                return false;
            }

            return true;
        };

        return (
            <div className="relative w-full">
                <NumericFormat
                    value={Array.isArray(value) ? value[0] : value}
                    inputMode="decimal"
                    name={name}
                    getInputRef={ref}
                    id={props.id ? props.id : name}
                    required={true}
                    className={cn(
                        'pl-2 pr-10 py-1.5 border-stroke form-input w-full text-xs text-grey text-right disabled:opacity-50 disabled:cursor-not-allowed',
                        className,
                    )}
                    autoComplete={'off'}
                    isAllowed={withValidCharacters}
                    onValueChange={(val) => {
                        if (setValue) setValue(val.floatValue ?? 0);
                    }}
                    allowNegative={false}
                    decimalScale={decimals.maxDecimals}
                    decimalSeparator={separators.decimalSeparator}
                    thousandSeparator={separators.thousandSeparator}
                    {...props}
                />
                <span className="flex absolute items-center gap-3 right-3 top-1.5 bottom-1.5 text-xs text-grey">
                    <span className="w-px h-4 bg-stroke"></span>
                    <span>€</span>
                </span>
            </div>
        );
    },
);

export default CurrencyInput;
